import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotifications, useNotificationActions } from '../../hooks/notifications';
import { NotificationStatusEnum } from '../../types/notification';
import { Button, LoadingSpinner } from '../UI';
import { ROUTES } from '../../constants/permissions';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const NOTIFICATIONS_PREVIEW_LIMIT = 8;

export const NotificationIcon: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { list, unreadCount, isLoading, refetch } = useNotifications();
  const { markAsRead, markAllAsRead } = useNotificationActions(refetch);

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const previewList = list.slice(0, NOTIFICATIONS_PREVIEW_LIMIT);
  const hasUnread = unreadCount > 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="header-notification-button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative p-2 rounded-xl admin-text-muted hover:admin-text-body hover:bg-gray-100 dark:hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
        aria-label={hasUnread ? `${unreadCount} notificações não lidas` : 'Notificações'}
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span
            data-testid="header-notification-badge"
            className="absolute top-1 right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          data-testid="header-notification-panel"
          className="absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-xl admin-border border admin-surface shadow-lg"
        >
          <div className="border-b admin-border px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold admin-text-title">Notificações</h3>
            {hasUnread && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => markAllAsRead()}
                className="text-xs"
              >
                Marcar todas como lidas
              </Button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="sm" />
              </div>
            ) : previewList.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm admin-text-muted">
                Nenhuma notificação
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {previewList.map((n) => (
                  <li key={n.id} className="px-4 py-3 admin-row-hover">
                    <div className="flex justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium admin-text-title truncate">{n.title}</p>
                        <p className="mt-0.5 text-xs admin-text-body line-clamp-2">{n.message}</p>
                        <p className="mt-1 text-[11px] admin-text-muted">
                          {formatDistanceToNow(new Date(n.createdAt), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                      {n.status === NotificationStatusEnum.UNREAD && (
                        <button
                          type="button"
                          onClick={() => markAsRead(n.id)}
                          className="shrink-0 text-xs font-medium admin-text-muted hover:admin-text-title underline"
                        >
                          Marcar lida
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t admin-border px-4 py-2 admin-surface-muted">
            <Link
              data-testid="header-notification-link-all"
              to={ROUTES.NOTIFICATIONS}
              onClick={() => setIsOpen(false)}
              className="block text-center text-sm font-medium admin-text-body hover:admin-text-title"
            >
              Ver todas
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
